// GENERATED BY portal-gen — DO NOT EDIT.
//
// Source: Entitlements & API Keys — entitlements.v1.yaml
// Regenerate with `npm run generate`; `npm run check` byte-diffs this file against a
// fresh run and fails the build on any difference.
//
// Every object below preserves the contract's `additionalProperties: false` as `.strict()`.
// That is a privacy control, not a typing convenience: a control-plane response that ever
// grows a content-bearing field must fail to parse, not render.

import { z } from "zod";

/** A suite product. Enum kept identical to Billing's `Product` type. */
export const Product = z.enum(["gateway", "smart", "guard", "plan"]);
export type Product = z.infer<typeof Product>;

/** A delivery surface. Net-new enum owned by B04 (master_context.md §Surfaces). */
export const Surface = z.enum(["browser_ext", "public_api", "mcp", "vscode", "antigravity", "codex"]);
export type Surface = z.infer<typeof Surface>;

export const CreateKeyRequest = z.object({
  label: z.string().min(1).max(64),
  product_scopes: z.array(Product).optional(),
  surface_scopes: z.array(Surface).optional(),
}).strict();
export type CreateKeyRequest = z.infer<typeof CreateKeyRequest>;

export const CreatedKey = z.object({
  id: z.string().uuid(),
  key_prefix: z.string(),
  key: z.string(),
  label: z.string().optional(),
  product_scopes: z.array(Product),
  surface_scopes: z.array(Surface),
  rotated_from: z.string().uuid().nullable().optional(),
  created_at: z.string().datetime(),
}).strict();
export type CreatedKey = z.infer<typeof CreatedKey>;

export const Plan = z.enum(["individual", "team", "enterprise"]);
export type Plan = z.infer<typeof Plan>;

/** Wire status from Billing. `locked` (trial expired / billing lock) hard-gates the org. */
export const SubscriptionStatus = z.enum(["trialing", "active", "past_due", "canceled", "locked"]);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatus>;

export const TrialState = z.enum(["none", "active", "expiring", "expired"]);
export type TrialState = z.infer<typeof TrialState>;

export const EntitlementSnapshot = z.object({
  org_id: z.string().uuid(),
  plan: Plan,
  status: SubscriptionStatus,
  products: z.array(Product),
  seat_limit: z.number().int(),
  seat_count: z.number().int(),
  seat_valid: z.boolean().optional(),
  trial_state: TrialState,
  trial_ends_at: z.string().datetime().nullable().optional(),
  interval: z.enum(["month", "year"]).nullable().optional(),
  current_period_end: z.string().datetime().nullable().optional(),
  source_occurred_at: z.string().datetime().optional(),
  degraded: z.boolean().optional(),
}).strict();
export type EntitlementSnapshot = z.infer<typeof EntitlementSnapshot>;

export const KeyStatus = z.enum(["active", "revoked"]);
export type KeyStatus = z.infer<typeof KeyStatus>;

export const KeySummary = z.object({
  id: z.string().uuid(),
  key_prefix: z.string(),
  label: z.string().optional(),
  status: KeyStatus,
  product_scopes: z.array(Product),
  surface_scopes: z.array(Surface),
  last_used_at: z.string().datetime().nullable().optional(),
  created_at: z.string().datetime(),
  revoked_at: z.string().datetime().nullable().optional(),
}).strict();
export type KeySummary = z.infer<typeof KeySummary>;

export const RotateKeyRequest = z.object({
  grace_seconds: z.number().int().optional(),  // default: 0
  product_scopes: z.array(Product).optional(),
  surface_scopes: z.array(Surface).optional(),
}).strict();
export type RotateKeyRequest = z.infer<typeof RotateKeyRequest>;

// ---- Operations ----
//
// `roleGate` is documentation carried into the type system, NOT enforcement. It lets a
// lint test assert that every admin-gated call site sits inside a RequireAdmin subtree,
// and it lets the client attach the right context to a 403.

/** List the org's API keys (metadata only; never the secret). */
export const listKeys = {
  id: "listKeys",
  method: "GET",
  path: "/v1/orgs/{orgId}/keys",
  roleGate: "any",
  pathParams: ["orgId"] as const,
  queryParams: {
    prefix: { required: false },
    status: { required: false },
  } as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 200,
  response: z.array(KeySummary),
  errors: [401, 403, 503] as const,
} as const;
export type ListKeysResponse = z.infer<typeof listKeys.response>;

/** Create an API key for the org. Returns the cleartext key ONCE. */
export const createKey = {
  id: "createKey",
  method: "POST",
  path: "/v1/orgs/{orgId}/keys",
  roleGate: "any",
  pathParams: ["orgId"] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: CreateKeyRequest,
  successStatus: 201,
  response: CreatedKey,
  errors: [401, 403, 404, 422, 503] as const,
} as const;
export type CreateKeyResponse = z.infer<typeof createKey.response>;

/** Rotate a key's secret. Returns the new cleartext key ONCE. */
export const rotateKey = {
  id: "rotateKey",
  method: "POST",
  path: "/v1/orgs/{orgId}/keys/{keyId}/rotate",
  roleGate: "any",
  pathParams: ["orgId", "keyId"] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: RotateKeyRequest,
  successStatus: 201,
  response: CreatedKey,
  errors: [401, 403, 404, 503] as const,
} as const;
export type RotateKeyResponse = z.infer<typeof rotateKey.response>;

/** Revoke a key immediately. */
export const revokeKey = {
  id: "revokeKey",
  method: "DELETE",
  path: "/v1/orgs/{orgId}/keys/{keyId}",
  roleGate: "any",
  pathParams: ["orgId", "keyId"] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 204,
  response: null,
  errors: [401, 403, 404, 503] as const,
} as const;

/** Resolved entitlement snapshot for the caller's own org (from the verified JWT). */
export const getMyEntitlements = {
  id: "getMyEntitlements",
  method: "GET",
  path: "/v1/entitlements/me",
  roleGate: "any",
  pathParams: [] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 200,
  response: EntitlementSnapshot,
  errors: [401, 503] as const,
} as const;
export type GetMyEntitlementsResponse = z.infer<typeof getMyEntitlements.response>;

export const entitlementsOperations = { listKeys, createKey, rotateKey, revokeKey, getMyEntitlements } as const;
